import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
export const isStandalone = window.matchMedia('(display-mode: standalone)').matches
  || ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true);

export const showIosInstallHint = isIos && !isStandalone;

// Every iOS browser is WebKit with no install prompt API; Add to Home Screen is reached through the
// Share sheet, whose button sits in a different place per browser. Firefox can't do it at all.
const iosBrowser: 'chrome' | 'edge' | 'firefox' | 'safari' =
  /crios/i.test(navigator.userAgent) ? 'chrome'
  : /edgios/i.test(navigator.userAgent) ? 'edge'
  : /fxios/i.test(navigator.userAgent) ? 'firefox'
  : 'safari';

export function IosInstallSteps() {
  if (iosBrowser === 'firefox') {
    return <>Firefox on iPhone can&apos;t install apps. Open this page in <strong>Safari</strong> or <strong>Chrome</strong>, then use Share &rarr; <strong>Add to Home Screen</strong>.</>;
  }
  const shareStep = iosBrowser === 'safari'
    ? <>Tap the <strong>&hellip;</strong> button at the bottom right if you see one, then tap <strong>Share</strong></>
    : iosBrowser === 'chrome' ? <>Tap the <strong>Share</strong> button in the address bar at the top right</>
    : <>Open the browser menu and tap <strong>Share</strong></>;
  return (
    <ol style={{ margin: 0, paddingLeft: 18 }}>
      <li>{shareStep}</li>
      <li style={{ marginTop: 6 }}>Scroll down the list and tap <strong>Add to Home Screen</strong> (tap <strong>View More</strong> first if it isn&apos;t shown)</li>
      <li style={{ marginTop: 6 }}>Tap <strong>Add</strong></li>
    </ol>
  );
}

interface PwaCtx {
  installPrompt: BeforeInstallPromptEvent | null;
  handleInstall: () => Promise<void>;
}

const Ctx = createContext<PwaCtx>({
  installPrompt: null,
  handleInstall: async () => {},
});

export function PwaProvider({ children }: { children: ReactNode }) {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    // Purge all SW caches whenever the app becomes visible (covers initial load
    // and resuming from background on iOS PWA). Safe to run repeatedly.
    function purgeCache() {
      if ('caches' in window) {
        void caches.keys().then(keys => keys.forEach(k => void caches.delete(k)));
      }
    }
    purgeCache();
    document.addEventListener('visibilitychange', purgeCache);
    // Reload when a new SW takes control so users always get fresh code.
    navigator.serviceWorker?.addEventListener('controllerchange', () => window.location.reload());

    const h = (e: Event) => { e.preventDefault(); setInstallPrompt(e as BeforeInstallPromptEvent); };
    window.addEventListener('beforeinstallprompt', h);
    window.addEventListener('appinstalled', () => setInstallPrompt(null));
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  async function handleInstall() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    setInstallPrompt(null);
  }

  return (
    <Ctx.Provider value={{ installPrompt, handleInstall }}>
      {children}
    </Ctx.Provider>
  );
}

export function usePwa() { return useContext(Ctx); }
